import axios from 'axios';
import { context, trace } from '@opentelemetry/api';
import { logger } from '@parrot-software/observability';

import type { WizardCompletionDto } from '@onboarding/application/dtos';
import type { IWizardStatusPort } from '@onboarding/application/ports';

const onbLog = logger.forDomain('ONB');
const tracer = trace.getTracer('@parrot-software/observability');

/**
 * Use case for marking the wizard as completed.
 *
 * @param props.wizardStatusPort - IWizardStatusPort implementation (injected)
 * @returns Use case executor
 *
 * @example
 * const useCase = completeWizardUseCase({ wizardStatusPort });
 * const result = await useCase.execute();
 */
export const completeWizardUseCase = (props: { wizardStatusPort: IWizardStatusPort }) => {
  const { wizardStatusPort } = props;

  const execute = async (): Promise<WizardCompletionDto> => {
    const span = tracer.startSpan('ONB.complete_wizard');
    const ctx = trace.setSpan(context.active(), span);

    return context.with(ctx, async () => {
      try {
        return await wizardStatusPort.completeWizard();
      } catch (error) {
        onbLog.critical('wizard_mark_failed', {
          'error.kind': (error as { constructor?: { name?: string } })?.constructor?.name ?? 'UnknownError',
          'error.message': error instanceof Error ? error.message : String(error),
          'http.status_code': axios.isAxiosError(error) ? error.response?.status : undefined,
        });
        throw error;
      } finally {
        span.end();
      }
    });
  };

  return { execute };
};
